import React, { useState, type ChangeEvent } from 'react'
import { Box, Card, CardContent, ClickAwayListener, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { SectionWrapper } from '../styles'
import {
  addSurveyQuestionInSection,
  type ISurveySection,
  setSectionDescription,
  setSectionTitle,
  setSelectedSection
} from '../../../store/slices/SurveyTemplateSlice'
import SurveyCreateQuestions from '../SurveyCreateQuestions'
import SurveyPreparedQuestions from '../SurveyPreparedQuestions'
import { SectionFiltersModal } from '../components/FiltersModal'
import AttributesFilterChips from '../../../components/AttributesFilterChips'
import SurveyItemDropdown from '../components/SurveyItemDropdown'
import SectionActions from '../components/SectionActions'
import { useAppDispatch } from '../../../store'
import SectionName from './SectionName'
import IsRepeatPerInstructor from '../components/IsRepeatPerInstructor'
import { Label } from './styles'
import { ReactComponent as BranchingIcon } from '../../../assets/branching.svg'
import { scrollToElement, updateElementBorderColor } from '../helpers'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import { BranchingOperation } from '../types'
import { grey } from '../../../colors'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { LookUpModal } from '../../AcademicUnitAndInstructorAddedQuestionsFeature/LookUpModal'

interface SurveySectionWrapperProps {
  section: ISurveySection
  sectionIndex: number
  surveySectionsList: ISurveySection[]
}

const SurveySectionWrapper: React.FC<SurveySectionWrapperProps> = ({ section, sectionIndex, surveySectionsList }) => {
  const [isLookUpModalOpen, setIsLookUpModalOpen] = useState<boolean>(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const parentSection = surveySectionsList.find(s => s.surveySectionId === section.parentSurveySectionId)

  const handleClickAway = (): void => {
    // TODO: Need to check and test handleClickAway sometime it creating weird behavior issues.
    // if (selectedSection) {
    //   dispatch(setSelectedSection(null))
    // }
  }

  const handleSectionTitleAndDescriptionChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    surveySectionId: string | undefined,
    setter: typeof setSectionTitle | typeof setSectionDescription
  ): void => {
    const { value } = event.target
    dispatch(setter({ surveySectionId, value }))
  }

  const onSectionClick = (): void => {
    dispatch(setSelectedSection(section))
  }

  const scrollToParentQuestion = (): void => {
    const questionId = section?.branchingFilters?.itemId
    scrollToElement(questionId, 'blue')
    // Remvoe color
    setTimeout(() => {
      updateElementBorderColor(questionId, '')
    }, 2000)
  }

  return (
    <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={handleClickAway}>
      <SectionWrapper
        key={section.surveySectionId}
        id={section.surveySectionId}
        onClick={onSectionClick}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <SectionName section={section} surveySectionsList={surveySectionsList} />
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={4} >

            {
              section?.branchingFilters?.itemType &&
              <Tooltip
                componentsProps={{ tooltip: { sx: { backgroundColor: 'transparent', width: '600px', maxWidth: '600px' } } }}
                onClick={scrollToParentQuestion}
                title={<Card sx={{ maxWidth: '400px' }}>
                  <CardContent >
                    <Typography variant="body1" >
                      <b>Parent Section Name</b>: {parentSection?.name}
                    </Typography>
                    <Typography variant="body1"><b>Item Type</b>: {section?.branchingFilters?.itemType}</Typography>
                    <Typography variant="body1"><b>Operation</b>: {section?.branchingFilters?.operation}</Typography>
                    <Typography variant="body1"><b>Prompt</b>: {section?.branchingFilters?.prompt}</Typography>
                    {section?.branchingFilters?.operation === BranchingOperation.Is &&

                      <Typography variant="body1">
                        <b>On Answers</b>:
                        {section?.branchingFilters?.answer?.map((answer: string, index: number) => (
                          <Box ml={8} key={index}>
                           - {answer}
                          </Box>
                        ))}
                      </Typography>
                    }
                  </CardContent>
                </Card>}>
                <GenericButton buttonSize={ButtonSize.Small} buttonType={ButtonType.Secondary} icon={<BranchingIcon />} />
              </Tooltip>
            }
            <GenericButton
              label={'Settings'}
              buttonSize={ButtonSize.Medium}
              buttonType={ButtonType.Secondary}
              fontWeight="bold"
              onClick={() => { setIsFilterModalOpen(true) }}
            />
            {
              isFilterModalOpen && <SectionFiltersModal section={section} isFilterModalOpen={isFilterModalOpen} closeFilterModal={() => { setIsFilterModalOpen(false) }} />
            }
          </Box>
        </Box>

        <Box display={'flex'} gap={4} alignItems={'center'} flexWrap="wrap">
          <IsRepeatPerInstructor section={section} />
          <AttributesFilterChips attributes={section?.attributes ?? []} />
        </Box>

        <Label> Display title (Optional) </Label>
        <TextField
          sx={{ marginTop: theme.spacing(2.5) }}
          fullWidth
          placeholder="e.g General questions"
          value={section?.title ?? ''}
          onChange={(e) => { handleSectionTitleAndDescriptionChange(e, section?.surveySectionId, setSectionTitle) }}
        />

        <Label> Section description (Optional) </Label>
        <TextField
          sx={{ marginY: theme.spacing(2.5) }}
          fullWidth
          placeholder="e.g. A simple description that captures the content of this section."
          value={section?.descr ?? ''}
          onChange={(e) => { handleSectionTitleAndDescriptionChange(e, section?.surveySectionId, setSectionDescription) }}
        />
        <SurveyPreparedQuestions surveyItems={section?.surveyItems} sectionIndex={sectionIndex} section={section} isSurveyTemplate={true} />
        <SurveyCreateQuestions sectionIndex={sectionIndex} section={section} />
        <Box
            display='flex'
            justifyContent='center'
            gap={theme.spacing(2)}
        >
          <SurveyItemDropdown sectionNumber={sectionIndex} />
          <Box mt={theme.spacing(6)}>
            <GenericButton
              buttonSize={ButtonSize.Medium}
              buttonType={ButtonType.Secondary}
              label='Lookup Question'
              startIcon={<SearchOutlinedIcon />}
              style={{ fontSize: '16px', fontWeight: 600, color: grey[950] }}
              onClick={() => { setIsLookUpModalOpen(true) }}
              disabled={!section?.surveySectionId}
            />
          </Box>
        </Box>
        {isLookUpModalOpen && (
          <LookUpModal
            lookUpData={{}}
            onClose={() => { setIsLookUpModalOpen(false) }}
            ignoreLimit={true}
            onAdd={({ removedQuestions, selectedQuestions }) => {
              if (section?.surveySectionId) {
                selectedQuestions.forEach(question => {
                  dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question, removedQuestions }))
                })
              }
              setIsLookUpModalOpen(false)
            }}
          />
        )}
        <SectionActions totalSections={surveySectionsList?.length} section={section} />
      </SectionWrapper>
    </ClickAwayListener>
  )
}

export default SurveySectionWrapper
